import {
	RiArrowRightSLine,
	RiCheckboxLine,
	RiCodeSSlashLine,
	RiDoubleQuotesL,
	RiH1,
	RiH2,
	RiH3,
	RiImageLine,
	RiLightbulbLine,
	RiListIndefinite,
	RiListOrdered,
	RiListUnordered,
	RiSeparator,
	RiTableLine,
	RiTextBlock,
} from "@remixicon/react";
import { KEYS, type TComboboxInputElement } from "platejs";
import type { PlateEditor, PlateElementProps } from "platejs/react";
import { PlateElement } from "platejs/react";
import type * as React from "react";
import { insertBlock } from "@/components/editor/transforms";
import {
	InlineCombobox,
	InlineComboboxContent,
	InlineComboboxEmpty,
	InlineComboboxGroup,
	InlineComboboxGroupLabel,
	InlineComboboxInput,
	InlineComboboxItem,
} from "../ui/inline-combobox";

type Group = {
	group: string;
	items: {
		icon: React.ReactNode;
		value: string;
		onSelect: (editor: PlateEditor, value: string) => void;
		focusEditor?: boolean;
		keywords?: string[];
		label?: string;
	}[];
};

const groups: Group[] = [
	{
		group: "Basic blocks",
		items: [
			{
				icon: <RiTextBlock className="size-4" />,
				keywords: ["paragraph"],
				label: "Text",
				value: KEYS.p,
			},
			{
				icon: <RiH1 className="size-4" />,
				keywords: ["title", "h1"],
				label: "Heading 1",
				value: KEYS.h1,
			},
			{
				icon: <RiH2 className="size-4" />,
				keywords: ["subtitle", "h2"],
				label: "Heading 2",
				value: KEYS.h2,
			},
			{
				icon: <RiH3 className="size-4" />,
				keywords: ["subtitle", "h3"],
				label: "Heading 3",
				value: KEYS.h3,
			},
			{
				icon: <RiListUnordered className="size-4" />,
				keywords: ["unordered", "ul", "-"],
				label: "Bulleted list",
				value: KEYS.ul,
			},
			{
				icon: <RiListOrdered className="size-4" />,
				keywords: ["ordered", "ol", "1"],
				label: "Numbered list",
				value: KEYS.ol,
			},
			{
				icon: <RiCheckboxLine className="size-4" />,
				keywords: ["checklist", "task", "checkbox", "[]"],
				label: "To-do list",
				value: KEYS.listTodo,
			},
			{
				icon: <RiArrowRightSLine className="size-4" />,
				keywords: ["collapsible", "expandable"],
				label: "Toggle",
				value: KEYS.toggle,
			},
			{
				icon: <RiCodeSSlashLine className="size-4" />,
				keywords: ["```"],
				label: "Code Block",
				value: KEYS.codeBlock,
			},
			{
				icon: <RiTableLine className="size-4" />,
				label: "Table",
				value: KEYS.table,
			},
			{
				icon: <RiDoubleQuotesL className="size-4" />,
				keywords: ["citation", "blockquote", "quote", ">"],
				label: "Blockquote",
				value: KEYS.blockquote,
			},
			{
				icon: <RiSeparator className="size-4" />,
				keywords: ["divider", "line", "separator", "---"],
				label: "Divider",
				value: KEYS.hr,
			},
			{
				icon: <RiLightbulbLine className="size-4" />,
				keywords: ["note", "info", "warning"],
				label: "Callout",
				value: KEYS.callout,
			},
		].map((item) => ({
			...item,
			onSelect: (editor: PlateEditor, value: string) => {
				insertBlock(editor, value, { upsert: true });
			},
		})),
	},
	{
		group: "Media",
		items: [
			{
				icon: <RiImageLine className="size-4" />,
				keywords: ["photo", "picture", "png", "jpg"],
				label: "Image",
				value: KEYS.img,
			},
		].map((item) => ({
			...item,
			onSelect: (editor: PlateEditor, value: string) => {
				insertBlock(editor, value);
			},
		})),
	},
	{
		group: "Advanced",
		items: [
			{
				icon: <RiListIndefinite className="size-4" />,
				keywords: ["toc"],
				label: "Table of Contents",
				value: KEYS.toc,
			},
		].map((item) => ({
			...item,
			onSelect: (editor: PlateEditor, value: string) => {
				insertBlock(editor, value, { upsert: true });
			},
		})),
	},
];

export function SlashInputElement(props: PlateElementProps<TComboboxInputElement>) {
	const { editor, element } = props;

	return (
		<PlateElement {...props} as="span">
			<InlineCombobox element={element} trigger="/">
				<InlineComboboxInput />

				<InlineComboboxContent>
					<InlineComboboxEmpty>No results</InlineComboboxEmpty>

					{groups.map(({ group, items }) => (
						<InlineComboboxGroup key={group}>
							<InlineComboboxGroupLabel>{group}</InlineComboboxGroupLabel>

							{items.map(({ focusEditor, icon, keywords, label, value, onSelect }) => (
								<InlineComboboxItem
									key={value}
									value={value}
									onClick={() => onSelect(editor, value)}
									label={label}
									focusEditor={focusEditor}
									group={group}
									keywords={keywords}
								>
									<div className="mr-2 text-muted-foreground">{icon}</div>
									{label ?? value}
								</InlineComboboxItem>
							))}
						</InlineComboboxGroup>
					))}
				</InlineComboboxContent>
			</InlineCombobox>

			{props.children}
		</PlateElement>
	);
}
